import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import {
  deleteEntity,
  disposePage,
  entityDeleted,
  getJobCollectionByDatasetId,
  loadEntity,
  loadPage,
  loadSystemEntity,
  removeEntityFromUI,
  saveEntity,
  setFormServerErrors,
  setFormValue,
  setPageValue,
  setPanes,
  setProcessing,
  testConnection,
} from "../../../redux/slices/datasetviewpageslice";
import { addNotification } from "../../../redux/slices/notificationsslice";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import {
  deepCompare,
  isNullOrEmpty,
  notificationDurationMiddle,
} from "../../../lib/utils";
import { Page } from "../../page";
import {
  setCurrentPage,
  setCurrentPageIsDirty,
} from "../../../redux/slices/currentpageslice";
import { BLOCK_DIRTY_MESSAGE } from "../../../settings";
import { DatasetViewPageBody } from "./datasetviewpagebody";
import { useEffectOnce } from "../../../hooks/useeffectonce";
import {
  hideAppDialog,
  setAppDialog,
} from "../../../redux/slices/appdialogslice";

export const DatasetViewPage = (props) => {
  const dispatch = useDispatch();
  const pageState = useSelector((state) => state.datasetViewPage);
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentPageState = useSelector((state) => state.currentPage);
  const appInit = useSelector((state) => state.appInit);
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  let currentPageId = "datasetViewPage";

  const location = useLocation();

  /*initial page load*/
  useEffect(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
        dispatch(getJobCollectionByDatasetId(id, t));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.create.loading_errormessage"),
                type: "error",
                closable: true,
                duration: 30000,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.create.unknown_error"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, [location.pathname]);
  /*initial load*/

  useEffectOnce(() => {
    let _id = id;

    if (!isNullOrEmpty(_id)) {
      _id = _id.toString().trim();

      handleOnEntitySelected(_id);
    }

    return () => {
      document.title = t("datasets.datasetview.intelligence");
      dispatch(disposePage(true));
    };
  }, [id]);

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    //exclude groupsids because when i remove then add elements the position in the array changes
    let { GroupsIds, ...fiv } = pageState.formInitialValues;
    let { GroupsIds: _GroupsIds, ...fv } = pageState.formValues;

    //let isDirty = !deepCompare(pageState.formInitialValues, pageState.formValues);
    let isDirty = !deepCompare(fiv, fv);
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*toolbar*/
  const handleOnClose = () => {
    if (blockNavigationIfDirty() == true) return;
    navigate("/datasets");
  };

  const handleOnNew = (e) => {
    if (blockNavigationIfDirty() == true) return;
    navigate("/createdataset");
  };

  const handleDataCorrection = (e) => { 
    if (blockNavigationIfDirty() == true) return;
    navigate("/datacorrection");
  };

  const handleOnSave = (e) => {
    //make sure no client errors; if so return
    // const isValidForm = formValidator(pageState.mode)(pageState.formValues) === undefined;
    // if (!isValidForm) {
    //   dispatch(
    //     addNotification({
    //       text: "The entity contains invalid information; hence, it cannot be persisted.",
    //       type: "error",
    //       closable: true,
    //       duration: 3,
    //       position: "topRight",
    //     })
    //   );
    //   return;
    // }
    //no client errors => proceed
    dispatch(saveEntity(t)).then(
      (response) => {
        dispatch(setCurrentPageIsDirty(false));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.create.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
            if (payload.data.ErrorCodes.EntityDoesNotExist) {
              dispatch(removeEntityFromUI(pageState.formValues.Id));
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: 3,
                  position: "topRight",
                })
              );
            } else dispatch(setFormServerErrors(errorMessages));
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.create.unknown_error");
          dispatch(setFormServerErrors([errorMessage]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleOnDelete = (referenceKey) => {
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "datasetslocator-page-are-you-sure",
        title: t("datasets.datasetview.warning"),
        content: t("datasets.datasetview.areyousure"),
        actions: {
          yesButton: {
            title: t("datasets.datasetview.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(referenceKey, t)).then(
                (response) => {},
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("datasets.create.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(entityDeleted(referenceKey));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessage = t("datasets.create.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                }
              );
            },
          },
          noButton: { title: t("datasets.datasetview.no")},
        },
      })
    );
  };

  const handleTestConnection = (e) => {
    const DatabaseType = pageState.formValues.DbConnection.DataBaseTypeId;
    const Server = pageState.formValues.DbConnection.Server;
    const Database = pageState.formValues.DbConnection.DataBase;
    const Username = pageState.formValues.DbConnection.Username;
    const Password = pageState.formValues.DbConnection.Password;

    if (
      DatabaseType === "" ||
      DatabaseType === undefined ||
      Server === "" ||
      Server === undefined ||
      Database === "" ||
      Database === undefined ||
      Username === "" ||
      Username === undefined ||
      Password === "" ||
      Password === undefined
    ) {
      dispatch(
        addNotification({
          text: t("datasets.datasetview.fill4"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return;
    }

    dispatch(testConnection(t)).then(
      (response) => {
        dispatch(setCurrentPageIsDirty(false));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.create.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
            if (payload.data.ErrorCodes.EntityDoesNotExist) {
              dispatch(removeEntityFromUI(pageState.formValues.Id));
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: 3,
                  position: "topRight",
                })
              );
            } else dispatch(setFormServerErrors(errorMessages));
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.create.unknown_error");
          dispatch(setFormServerErrors([errorMessage]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };
  /*toolbar*/

  /*view/edit entity*/
  const handleOnEntitySelected = (entityId, item) => {
    if (blockNavigationIfDirty() == true) return;

    if (Number(id) !== entityId) {
      navigate(`/datasetview/${entityId}`, { replace: true }); // Adjust path if needed
    } else {
      return;
    }

    dispatch(loadEntity(entityId, t)).then(
      (response) => {
        dispatch(
          setFormValue({
            id: "Id",
            value: entityId,
          })
        );
      },
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.create.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist)
              dispatch(removeEntityFromUI(entityId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.create.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };
  const handleOnSystemEntitySelected = (entityId, item) => {
    if (blockNavigationIfDirty() == true) return;

    if (Number(id) !== entityId) {
      navigate(`/datasetview/${entityId}`, { replace: true }); // Adjust path if needed
    } else {
      return;
    }

    dispatch(loadSystemEntity(entityId, t)).then(
      (response) => {
        dispatch(
          setFormValue({
            id: "Id",
            value: entityId,
          })
        );
      },
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.create.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist)
              dispatch(removeEntityFromUI(entityId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.create.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  /*view/edit entity*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/
  //tools visible/hidden
  const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "100%",
            collapsible: false,
          },
        ])
      );
    else
      dispatch(
        setPanes([
          {
            size: "20%",
            min: "20%",
            max: "25%",
            collapsible: false,
          },
          {
            min: "70%",
            max: "80%",
            collapsible: false,
          },
        ])
      );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
  };
  return (
    <Fragment>
      <Page
        id="datasetsViewPage"
        className="datasetsview-page"
        title={t("datasets.datasetview.datasetviews")}
        animation={props.animation}
      >
        <DatasetViewPageBody    
          onSplitterChange={handleOnSplitterChange}
          onToolsToggle={handleOnToolsToggle}
          onSave={handleOnSave}
          onDelete={handleOnDelete}
          onDataCorrection={handleDataCorrection}
          onClose={handleOnClose}
          // onActivateDeactivate={handleOnActivateDeactivate}
          // onErrorbarClose={handleOnErrorbarClose} onGroupCheck={handleOnGroupCheck}
          onTestConnection={handleTestConnection}
          onNew={handleOnNew}
          onEntitySelected={handleOnEntitySelected}
          onEntityDoubleClick={handleOnEntitySelected}
          onSystemEntitySelected={handleOnSystemEntitySelected}
          onSystemEntityDoubleClick={handleOnSystemEntitySelected}
          updateDirtyState={updateDirtyState}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .datasetsview-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .datasetsview-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .datasetsview-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .datasetsview-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .datasetsview-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    .datasetsview-page > .content .datasetsview-page-manager{   
                        height:100%;                     
                        background-color: rgba(0, 0, 0, 0.02);
                    }
                    `}
      </style>
    </Fragment>
  );
};
